import React, { useCallback, useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from '@shared/config/paths';
import { useRefetchQueries } from '@shared/hooks/useRefetchQueries';
import { parseDate } from '@/shared/utils/common';
import FullScreenLayout from '@/shared/components/layout/FullScreenLayout';
import RadioGroup from '@/shared/components/molecules/RadioGroup';
import StyledCalendar from '@shared/components/atoms/StyledCalendar';
import StyledInput from '@shared/components/atoms/StyledInput';
import { FormField, FieldLabel, FieldSection, FlexBox } from '@/shared/components/atoms/common.style';
import { AdditionalRecruitEndType, AdditionalRecruitmentInfoResponseDto } from '@api/generated/model';
import {
	getContestAdminControllerGetAdditionalRecruitmentInfoV1QueryKey,
	getContestAdminControllerGetContestDetailV1QueryKey,
	useContestAdminControllerGetAdditionalRecruitmentInfoV1,
	useContestAdminControllerSetAdditionalRecruitmentV1
} from '@/api/generated/admin-contest/admin-contest';
import Loading from '@/shared/components/molecules/Loading';
import theme from '@/styles/theme';
// import { AxiosError } from 'axios';
// import type { ErrorResponse } from '@api/axiosInstance';

type AdditionalEntryPeriodType = {
	additionalEntryStartDate: string;
	additionalEntryEndDate: string;
};

const END_TYPE_OPTIONS = [
	{ title: '자동 종료', value: AdditionalRecruitEndType.AUTO_CLOSE },
	{ title: '기간 진행 후 종료', value: AdditionalRecruitEndType.PERIOD_CLOSE }
];

const ContestAdditionalRecruitment = () => {
	const { contestId = '' } = useParams<{ contestId: string }>();
	const navigate = useNavigate();
	const refetchQueries = useRefetchQueries();

	const [dates, setDates] = useState<AdditionalEntryPeriodType>({
		additionalEntryStartDate: '',
		additionalEntryEndDate: ''
	});
	const [endType, setEndType] = useState<AdditionalRecruitEndType>(AdditionalRecruitEndType.AUTO_CLOSE);

	const { isLoading, data } = useContestAdminControllerGetAdditionalRecruitmentInfoV1<AdditionalRecruitmentInfoResponseDto>(contestId, {
		query: { enabled: !!contestId }
	});

	const { mutate: setAdditionalRecruitment, isPending } = useContestAdminControllerSetAdditionalRecruitmentV1({
		mutation: {
			onSuccess: async () => {
				toast.success('추가 모집이 설정되었습니다.');
				await refetchQueries(
					getContestAdminControllerGetContestDetailV1QueryKey(contestId),
					getContestAdminControllerGetAdditionalRecruitmentInfoV1QueryKey(contestId)
				);
				navigate(paths.contest.detail.to({ contestId }));
			}
		}
	});

	const handleEndTypeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setEndType(e.target.value as AdditionalRecruitEndType);
		e.target.checked = false;
	}, []);

	const handleSave = () => {
		const { additionalEntryStartDate, additionalEntryEndDate } = dates;

		if (!data?.canSetup) {
			toast.error(data?.reason ?? '추가 모집을 설정할 수 없는 대회입니다.');
			return;
		}
		if (!additionalEntryStartDate || !additionalEntryEndDate) {
			toast.error('추가 모집 기간을 입력해주세요.');
			return;
		}

		setAdditionalRecruitment({
			contestId,
			data: {
				additionalEntryStartDate,
				additionalEntryEndDate,
				endType
			}
		});
	};

	const onChangeDate = useCallback((date: Date | null, name: keyof AdditionalEntryPeriodType) => {
		setDates(prev => {
			if (!date) return { ...prev, [name]: '' };
			return { ...prev, [name]: date.toISOString() };
		});
	}, []);

	if (isLoading) return <Loading />;
	if (!data) return null;

	return (
		<FullScreenLayout onClickButton={handleSave} loading={isPending}>
			{!data.canSetup && (
				<FormField>
					<FieldSection>
						<WarningText>{data.reason ?? '현재 추가 모집을 설정할 수 없는 상태입니다.'}</WarningText>
					</FieldSection>
				</FormField>
			)}
			<FormField>
				<FieldLabel>추가 모집 기간</FieldLabel>
				<FieldSection>
					<FlexBox>
						<StyledCalendar
							title="기간 설정"
							value={parseDate(dates.additionalEntryStartDate)}
							onChange={date => onChangeDate(date, 'additionalEntryStartDate')}
							placeholder="시작일"
							minDate={new Date()}
							disabled={!data.canSetup}
						/>
						<StyledCalendar
							value={parseDate(dates.additionalEntryEndDate)}
							onChange={date => onChangeDate(date, 'additionalEntryEndDate')}
							placeholder="종료일"
							minDate={parseDate(dates.additionalEntryStartDate, { addDays: 1 })}
							maxDate={parseDate(data.startDate, { minusDays: 7 })}
							disabled={!data.canSetup}
						/>
					</FlexBox>
				</FieldSection>
			</FormField>
			<FormField>
				<FieldLabel>종료 방식</FieldLabel>
				<FieldSection>
					<RadioGroup
						title="추가 모집 종료 방식"
						description="설정된 추가 모집 기간만큼 진행 후 종료됩니다."
						descriptionColor={theme.colors.activeRedOrange}
						name="endType"
						value={endType}
						items={END_TYPE_OPTIONS}
						onChange={handleEndTypeChange}
						disabled={!data.canSetup}
					/>
				</FieldSection>
			</FormField>
			<FormField>
				<FieldLabel>코스별 잔여좌석</FieldLabel>
				<FieldSection>
					{data.courses.map(course => (
						<FlexBox key={course.courseId}>
							<StyledInput title="코스명" width={305} value={course.name} disabled={true} />
							<StyledInput
								title="잔여 모집인원(명)"
								width={142}
								margin="0 20px"
								value={course.vacancy.toLocaleString()}
								disabled={true}
							/>
							<StyledInput title="가격(원)" width={142} value={course.price.toLocaleString()} disabled={true} />
						</FlexBox>
					))}
				</FieldSection>
			</FormField>
		</FullScreenLayout>
	);
};

export default ContestAdditionalRecruitment;

const WarningText = styled.p`
	font-size: 14px;
	color: ${theme.colors.activeRedOrange};
`;
